import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { SUPPORTED_LNGS, LNG_LABELS } from '../i18n';
import './Settings.css';

export default function Settings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    user, logout, updateMe, uploadAvatar,
    changePassword, changeEmail, deleteAccount,
  } = useAuth();

  const fileInputRef = useRef(null);
  const [bio, setBio] = useState('');
  const [website, setWebsite] = useState('');
  const [location, setLocation] = useState('');
  const [profileMsg, setProfileMsg] = useState(null); // { kind, text }

  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNext, setPwNext] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwMsg, setPwMsg] = useState(null);

  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailMsg, setEmailMsg] = useState(null);

  const [delModal, setDelModal] = useState(false);
  const [delPw, setDelPw] = useState('');
  const [delMsg, setDelMsg] = useState(null);

  const [notifMsg, setNotifMsg] = useState(null);
  const [notifBusy, setNotifBusy] = useState(false);

  const [commsLangMsg, setCommsLangMsg] = useState(null);
  const [commsLangBusy, setCommsLangBusy] = useState(false);

  const [privacyMsg, setPrivacyMsg] = useState(null);
  const [privacyBusy, setPrivacyBusy] = useState(null); // clave en curso

  useEffect(() => {
    if (!user) return;
    setBio(user.bio || '');
    setWebsite(user.website || '');
    setLocation(user.location || '');
  }, [user]);

  if (!user) return <div className="settings-state">{t('settings.loading')}</div>;

  const onPickAvatar = () => fileInputRef.current?.click();
  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfileMsg(null);
    try { await uploadAvatar(file); setProfileMsg({ kind: 'ok', text: t('settings.msg.photo_ok') }); }
    catch (err) { setProfileMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.photo_err') }); }
    finally { e.target.value = ''; }
  };

  const saveProfile = async () => {
    setProfileMsg(null);
    try { await updateMe({ bio, website, location: location.trim() }); setProfileMsg({ kind: 'ok', text: t('settings.msg.profile_ok') }); }
    catch (err) { setProfileMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.profile_err') }); }
  };

  const submitPassword = async () => {
    setPwMsg(null);
    if (!pwCurrent || !pwNext) return setPwMsg({ kind: 'err', text: t('settings.msg.pw_required') });
    if (pwNext.length < 6)      return setPwMsg({ kind: 'err', text: t('settings.msg.pw_short') });
    if (pwNext !== pwConfirm)   return setPwMsg({ kind: 'err', text: t('settings.msg.pw_mismatch') });
    try {
      await changePassword(pwCurrent, pwNext);
      setPwCurrent(''); setPwNext(''); setPwConfirm('');
      setPwMsg({ kind: 'ok', text: t('settings.msg.pw_ok') });
    } catch (err) {
      setPwMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.pw_err') });
    }
  };

  const submitEmail = async () => {
    setEmailMsg(null);
    if (!newEmail || !emailPw) return setEmailMsg({ kind: 'err', text: t('settings.msg.email_required') });
    try {
      await changeEmail(newEmail, emailPw);
      setNewEmail(''); setEmailPw('');
      setEmailMsg({ kind: 'ok', text: t('settings.msg.email_ok') });
    } catch (err) {
      setEmailMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.email_err') });
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const toggleMessageEmails = async () => {
    if (notifBusy) return;
    setNotifBusy(true); setNotifMsg(null);
    try {
      await updateMe({ notifyMessagesEmail: user.notifyMessagesEmail === false });
      setNotifMsg({ kind: 'ok', text: t('settings.msg.notif_ok') });
    } catch (err) {
      setNotifMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.notif_err') });
    } finally { setNotifBusy(false); }
  };

  // Idioma de los correos de Troly. Es independiente del idioma de navegación
  // (el del Navbar): 'auto' deja que el backend lo deduzca de la ubicación.
  const changeCommsLang = async (value) => {
    if (commsLangBusy) return;
    setCommsLangBusy(true); setCommsLangMsg(null);
    try {
      await updateMe({ emailLanguage: value });
      setCommsLangMsg({ kind: 'ok', text: t('settings.msg.comms_lang_ok') });
    } catch (err) {
      setCommsLangMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.comms_lang_err') });
    } finally { setCommsLangBusy(false); }
  };

  // Privacidad del perfil. Los tres flags son aditivos y su default es
  // permisivo (`!== false`), así que las cuentas existentes no cambian.
  const togglePrivacy = async (key, nextValue) => {
    if (privacyBusy) return;
    setPrivacyBusy(key); setPrivacyMsg(null);
    try {
      await updateMe({ [key]: nextValue });
      setPrivacyMsg({ kind: 'ok', text: t('settings.msg.privacy_ok') });
    } catch (err) {
      setPrivacyMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.privacy_err') });
    } finally { setPrivacyBusy(null); }
  };

  const submitDelete = async () => {
    setDelMsg(null);
    if (!delPw) return setDelMsg({ kind: 'err', text: t('settings.msg.del_pw_required') });
    try { await deleteAccount(delPw); navigate('/'); }
    catch (err) { setDelMsg({ kind: 'err', text: err.response?.data?.message || t('settings.msg.del_err') }); }
  };

  return (
    <div className="settings">
      <Helmet><title>{t('settings.meta_title')}</title></Helmet>
      <div className="settings-body container">
        <h1 className="settings-h1">{t('settings.title')}</h1>

        {/* ── Avatar + perfil ── */}
        <section className="set-card">
          <h2 className="set-card__title">{t('settings.photo_profile')}</h2>

          <div className="set-avatar-row">
            <img
              src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=45E6C2&color=0A0A0A&size=128`}
              alt={user.name}
              className="set-avatar"
            />
            <div>
              <button className="btn-teal" onClick={onPickAvatar}>{t('settings.upload_photo')}</button>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                ref={fileInputRef}
                onChange={onAvatarChange}
                style={{ display: 'none' }}
              />
              <p className="set-hint">{t('settings.photo_hint')}</p>
            </div>
          </div>

          <label className="set-label">{t('settings.bio_label')}</label>
          <textarea
            className="set-textarea"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder={t('settings.bio_placeholder')}
          />
          <p className="set-hint">{bio.length}/300</p>

          <label className="set-label">{t('settings.location_label')}</label>
          <input
            className="set-input"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={100}
            placeholder={t('settings.location_placeholder')}
            autoComplete="address-level2"
          />
          <p className="set-hint">{t('settings.location_hint')}</p>

          <label className="set-label">{t('settings.website_label')}</label>
          <input
            className="set-input"
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://..."
          />

          {profileMsg && <Msg msg={profileMsg} />}
          <button className="btn-teal" onClick={saveProfile}>{t('settings.save_profile')}</button>
        </section>

        {/* ── Contraseña ── */}
        <section className="set-card">
          <h2 className="set-card__title">{t('settings.change_password')}</h2>
          <label className="set-label">{t('settings.current_password')}</label>
          <input className="set-input" type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} autoComplete="current-password" />
          <label className="set-label">{t('settings.new_password')}</label>
          <input className="set-input" type="password" value={pwNext} onChange={(e) => setPwNext(e.target.value)} autoComplete="new-password" />
          <label className="set-label">{t('settings.confirm_new_password')}</label>
          <input className="set-input" type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} autoComplete="new-password" />
          {pwMsg && <Msg msg={pwMsg} />}
          <button className="btn-teal" onClick={submitPassword}>{t('settings.update_password')}</button>
        </section>

        {/* ── Correo ── */}
        <section className="set-card">
          <h2 className="set-card__title">{t('settings.change_email')}</h2>
          <p className="set-hint" style={{ marginBottom: 12 }}>{t('settings.current_email')} <strong>{user.email}</strong></p>
          <label className="set-label">{t('settings.new_email')}</label>
          <input className="set-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
          <label className="set-label">{t('settings.confirm_password')}</label>
          <input className="set-input" type="password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} autoComplete="current-password" />
          {emailMsg && <Msg msg={emailMsg} />}
          <button className="btn-teal" onClick={submitEmail}>{t('settings.change_email_btn')}</button>
        </section>

        {/* ── Privacidad ── */}
        <section className="set-card">
          <h2 className="set-card__title">{t('settings.privacy')}</h2>

          <SetToggle
            checked={user.isPublicProfile !== false}
            disabled={privacyBusy === 'isPublicProfile'}
            onChange={() => togglePrivacy('isPublicProfile', user.isPublicProfile === false)}
            label={t('settings.privacy_public_label')}
            hint={t('settings.privacy_public_hint')}
          />

          <SetToggle
            checked={user.showTotalValue !== false}
            disabled={privacyBusy === 'showTotalValue'}
            onChange={() => togglePrivacy('showTotalValue', user.showTotalValue === false)}
            label={t('settings.privacy_total_label')}
            hint={t('settings.privacy_total_hint')}
          />

          <SetToggle
            checked={user.showItemPrices !== false}
            disabled={privacyBusy === 'showItemPrices'}
            onChange={() => togglePrivacy('showItemPrices', user.showItemPrices === false)}
            label={t('settings.privacy_prices_label')}
            hint={t('settings.privacy_prices_hint')}
          />

          {privacyMsg && <Msg msg={privacyMsg} />}
        </section>

        {/* ── Notificaciones ── */}
        <section className="set-card">
          <h2 className="set-card__title">{t('settings.notifications')}</h2>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={user.notifyMessagesEmail !== false}
              onChange={toggleMessageEmails}
              disabled={notifBusy}
              style={{ marginTop: 3, accentColor: 'var(--troly-jade, #45E6C2)' }}
            />
            <span>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>
                {t('settings.notif_messages_label')}
              </span>
              <span className="set-hint" style={{ display: 'block', marginTop: 2 }}>
                {t('settings.notif_messages_hint')}
              </span>
            </span>
          </label>
          {notifMsg && <Msg msg={notifMsg} />}

          <label className="set-label" style={{ marginTop: 18 }} htmlFor="comms-lang">
            {t('settings.comms_lang_label')}
          </label>
          <select
            id="comms-lang"
            className="set-input"
            value={user.emailLanguage || 'auto'}
            onChange={(e) => changeCommsLang(e.target.value)}
            disabled={commsLangBusy}
          >
            <option value="auto">{t('settings.comms_lang_auto')}</option>
            {SUPPORTED_LNGS.map((lng) => (
              <option key={lng} value={lng}>{LNG_LABELS[lng]}</option>
            ))}
          </select>
          <p className="set-hint">{t('settings.comms_lang_hint')}</p>
          {commsLangMsg && <Msg msg={commsLangMsg} />}
        </section>

        {/* ── Sesión y cuenta ── */}
        <section className="set-card">
          <h2 className="set-card__title">{t('settings.session')}</h2>
          <button className="btn-outline" onClick={handleLogout}>{t('settings.logout')}</button>
        </section>

        <section className="set-card set-card--danger">
          <h2 className="set-card__title">{t('settings.cancel_account')}</h2>
          <p className="set-hint">{t('settings.cancel_account_warn')}</p>
          <button className="btn-danger" onClick={() => setDelModal(true)}>{t('settings.cancel_my_account')}</button>
        </section>
      </div>

      {delModal && (
        <div className="modal-overlay" onClick={() => { setDelModal(false); setDelMsg(null); setDelPw(''); }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{t('settings.modal_title')}</h3>
            <p>{t('settings.modal_warn')}</p>
            <label className="set-label">{t('settings.modal_confirm_label')}</label>
            <input className="set-input" type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} autoComplete="current-password" />
            {delMsg && <Msg msg={delMsg} />}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button className="btn-outline" style={{ flex: 1 }} onClick={() => { setDelModal(false); setDelMsg(null); setDelPw(''); }}>
                {t('settings.back')}
              </button>
              <button className="btn-danger" style={{ flex: 1 }} onClick={submitDelete}>
                {t('settings.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Checkbox + etiqueta + ayuda. Touch target de 44px y sin estados hover-only.
function SetToggle({ checked, disabled, onChange, label, hint }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      minHeight: 44, padding: '8px 0', cursor: disabled ? 'default' : 'pointer',
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--troly-action)' }}
      />
      <span>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{label}</span>
        <span className="set-hint" style={{ display: 'block', marginTop: 2 }}>{hint}</span>
      </span>
    </label>
  );
}

function Msg({ msg }) {
  return (
    <div className={`set-msg set-msg--${msg.kind}`}>{msg.text}</div>
  );
}
